import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api, type AnswerResult, type Bundle, type Child, type Question, type SessionInfo } from '../api'
import { editDistance, mark } from '../lib/marking'
import { outbox } from '../lib/outbox'
import { sessionStore } from '../lib/sessionStore'
import { radius, theme } from '../theme'
import { Button, Card, Page, Progress } from '../components/ui'
import { Shell } from '../components/Shell'
import { Trace, TraceRegion } from '../lib/trace'
import { ErrorScreen, Loading, OfflineBar } from '../components/States'
import { Trial } from './trials'
import { HAS_KEYBOARD, useTrialKeys } from '../lib/keys'
import { sfx } from '../lib/sound'
import { say } from '../lib/speech'

const MODULE_NAMES: Record<string, string> = {
  spelling: 'Spelling',
  meaning: 'Meaning',
  reading: 'Reading',
  writing: 'Writing',
  play: 'Play',
}

/**
 * The whole practice as one bar cut into its parts, each part as wide as its
 * questions. The part being worked on is outlined, and the count under the
 * bar is for the whole practice, so it never reads "10 of 10" with a part
 * still to come. Like the plain bar, it only ever fills (D-048).
 */
function PartsBar({
  map, done, current,
}: { map: SessionInfo['map']; done: number; current: number }) {
  const total = map.reduce((n, s) => n + s.count, 0)
  return (
    <div>
      <div role="progressbar" aria-valuenow={done} aria-valuemax={total}
        style={{ display: 'flex', gap: 4 }}>
        {map.map((s, i) => {
          const filled = Math.min(s.count, Math.max(0, done - s.start))
          return (
            <div key={s.start} title={MODULE_NAMES[s.module] ?? s.module} style={{
              flex: s.count, height: 10, background: theme.orangeWarm, borderRadius: radius.pill,
              overflow: 'hidden', boxShadow: i === current ? `0 0 0 2px ${theme.orange}` : 'none',
            }}>
              <div style={{
                width: `${(filled / s.count) * 100}%`, height: '100%', background: theme.orange,
                transition: 'width 220ms',
              }} />
            </div>
          )
        })}
      </div>
      <div style={{ fontSize: 13, color: theme.faint, marginTop: 6, textAlign: 'right' }}>
        {done} of {total} done in all
      </div>
    </div>
  )
}

/**
 * Runs one session, question by question, on the device.
 *
 * Offline-first (Sam, 9 Oct 2026). The whole session comes down in one
 * download when it opens, and is kept in the browser. Each answer is marked
 * here, at once, and put in the outbox (lib/outbox.ts), which sends it to the
 * server in the background and keeps trying until the server has it. A lost
 * connection mid-session costs nothing: the child carries on, and the
 * answers go when it comes back. The server marks every answer again when it
 * arrives; its mark is the one recorded.
 *
 * Until 9 Oct each question was fetched on its own and each answer waited
 * for the server, so a dropped connection lost the answer. See
 * research/how-it-works.md, section 5.
 */
export function Session({
  child, session, onLeave, onFinished,
}: {
  child: Child
  session: SessionInfo
  onLeave: () => void
  onFinished: (sessionId: string) => void
}) {
  const sid = session.session_id
  // The server's cursor only moves when an answer reaches it, so with
  // answers still in the outbox the device is further on.
  const [position, setPosition] = useState(() => Math.max(session.cursor, sessionStore.cursor(sid)))
  const [bundle, setBundle] = useState<Bundle | null>(() => sessionStore.bundle(sid))
  const [result, setResult] = useState<AnswerResult | null>(null)
  const [skipped, setSkipped] = useState(false)
  const [reported, setReported] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [pending, setPending] = useState(() => outbox.count(sid))
  const [online, setOnline] = useState(
    typeof navigator === 'undefined' ? true : navigator.onLine,
  )
  const startedAt = useRef<number>(Date.now())
  // Each question is answered once: a fast double tap must not queue two.
  const answered = useRef<number>(-1)

  // Tells the update check in main.tsx not to reload mid-session.
  useEffect(() => {
    (window as { __wsInSession?: boolean }).__wsInSession = true
    return () => { (window as { __wsInSession?: boolean }).__wsInSession = false }
  }, [])
  useEffect(() => {
    const up = () => setOnline(true)
    const down = () => setOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])

  useEffect(() => outbox.subscribe(() => setPending(outbox.count(sid))), [sid])

  // The whole session, once. A copy already on the device is used straight
  // away; a fresh one from the server replaces it when it comes.
  const fetchBundle = useCallback(() => {
    setError(null)
    api.bundle(sid)
      .then((b) => { sessionStore.saveBundle(b); setBundle(b) })
      .catch((err) => { if (!sessionStore.bundle(sid)) setError(err) })
  }, [sid])
  useEffect(fetchBundle, [fetchBundle])

  const question: Question | null = useMemo(() => {
    const q = bundle?.questions[position]
    if (!q) return null
    const { key: _key, ...shown } = q
    return shown
  }, [bundle, position])

  useEffect(() => {
    // Nothing left to ask: the summary waits for the outbox to empty.
    if (position >= session.planned_count) {
      onFinished(sid)
      return
    }
    setResult(null)
    setSkipped(false)
    setReported(false)
    startedAt.current = Date.now()
  }, [position, session.planned_count, sid, onFinished])

  const answer = (response: unknown, extras: Record<string, unknown> = {}) => {
    const q = bundle?.questions[position]
    if (!q || answered.current === position) return
    answered.current = position
    const isSkip = Boolean(extras.skipped)
    setSkipped(isSkip)
    const full = { ...q.payload, ...q.key }
    // Marked here, the same way the server marks it (lib/marking.ts).
    const isCorrect = isSkip
      ? (mark(q.item_type, full, '') === null ? null : false)
      : mark(q.item_type, full, response)
    const distance = !isSkip && (q.item_type === 'cued_recall' || q.item_type === 'free_recall')
      && typeof response === 'string'
      ? editDistance(response, String(q.key.answer ?? ''))
      : null
    outbox.add({
      sessionId: sid,
      learnerId: child.learner_id,
      body: {
        position,
        response,
        duration_sec: (Date.now() - startedAt.current) / 1000,
        answered_at: new Date().toISOString(),
        ...extras,
      },
    })
    sessionStore.setCursor(sid, position + 1)
    const body: AnswerResult = {
      trial_id: '',
      is_correct: isCorrect,
      is_first_attempt: true,
      edit_distance: distance,
      correct_answer: (q.key.answer ?? null) as AnswerResult['correct_answer'],
      next_position: position + 1,
      finished: position + 1 >= session.planned_count,
      about: q.key.about ?? null,
    }
    setResult(body)
    // The chime belongs to the moment the answer is judged. A skipped
    // question gets no sound at all: skipping is a choice, not a mistake,
    // and a "wrong" tone for it would punish honesty.
    if (body.is_correct === true) sfx.correct()
    else if (body.is_correct === false && !isSkip) sfx.notYet()
    // Nothing to mark means nothing to show. Move straight on.
    if (body.is_correct === null) {
      if (body.finished) onFinished(sid)
      else setPosition(body.next_position)
    }
  }

  const next = () => {
    if (result?.finished) onFinished(sid)
    else if (result) setPosition(result.next_position)
  }

  const leave = async () => {
    try { await api.leave(sid) } catch { /* going home regardless */ }
    onLeave()
  }

  if (error) {
    return <ErrorScreen error={error} onRetry={fetchBundle} onHome={onLeave} />
  }
  if (!question) return <Loading what="Getting your questions ready" />

  const moduleName = MODULE_NAMES[question.module] ?? question.module
  const stopIndex = session.map.findIndex((s) => position >= s.start && position <= s.end)
  const stop = stopIndex >= 0 ? session.map[stopIndex] : undefined
  const withinStop = stop ? position - stop.start + 1 : position + 1
  const stopTotal = stop ? stop.count : session.planned_count
  // "10 of 10" used to be the only count, and it was this part's, under a
  // bar for the whole practice (Sam, 22 Oct 2026: Charles saw 10/10 with
  // more to come). Now the step line names the part, and the bar shows both.
  const parts = session.map.length

  return (
    <>
      <OfflineBar online={online} pending={pending} durable={outbox.durable()} />
      <Page width={560}>
        <Shell
          title={
            <>
              <Trace id="T2.display_name">{child.display_name}</Trace>
              {' · '}
              <Trace id="T13.module" title="from the plan row, not worked out here">
                {moduleName}
              </Trace>
            </>
          }
          step={
            <Trace id="calc.position within this stop of the T12 session map">
              {parts > 1
                ? `Part ${stopIndex + 1} of ${parts} · question ${withinStop} of ${stopTotal}`
                : `Question ${withinStop} of ${stopTotal}`}
            </Trace>
          }
          exitMode="leave"
          onExit={leave}
          right={
            question.item_version_id ? (
              <button
                type="button"
                title="Tell a grown-up this question is strange. It will not be shown again."
                disabled={reported}
                onClick={() => {
                  if (question.item_version_id && !reported) {
                    setReported(true)
                    api.flag(session.session_id, question.item_version_id)
                      .catch(() => undefined)
                  }
                }}
                style={{
                  fontFamily: 'inherit', fontSize: 13,
                  color: reported ? theme.greenDark : theme.faint,
                  background: reported ? theme.greenPale : 'transparent',
                  border: `1px solid ${reported ? theme.green : theme.border}`,
                  borderRadius: radius.sm, padding: '6px 10px',
                  cursor: reported ? 'default' : 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {reported ? '✓ Reported' : '🚩 Report'}
              </button>
            ) : null
          }
        >
          <div style={{ marginBottom: 18 }}>
            <Trace id="T12.cursor" block
                   title="the bar only ever fills; nothing here can fall (D-048)">
              {parts > 1
                ? <PartsBar map={session.map} done={position} current={stopIndex} />
                : <Progress done={position} total={session.planned_count} />}
            </Trace>
          </div>

          {result && result.is_correct !== null ? (
            <Verdict result={result} skipped={skipped} onNext={next} />
          ) : (
            <TraceRegion id="T14" writes
              note="your answer is saved as a new trial row (response, is_correct, duration_sec), then T8 mastery_state, T15 question_history and T7 learner_word are updated">
              <TraceRegion id="T10" note="the question itself: item_version.payload_json">
                <Trial
                  q={question}
                  firstLanguage={child.first_language}
                  onAnswer={answer}
                  busy={false}
                />
              </TraceRegion>
            </TraceRegion>
          )}
        </Shell>
      </Page>
    </>
  )
}

/**
 * What happens after an answer.
 *
 * A mistake is information, not a telling-off. It shows the right answer,
 * names what was typed, and moves on without ceremony.
 */
function Verdict({
  result, skipped, onNext,
}: { result: AnswerResult; skipped: boolean; onNext: () => void }) {
  const right = result.is_correct === true

  // The verdict is read, not answered, so Enter means "next question". This
  // is the press that matters most: without it a keyboard run breaks for the
  // mouse between every single question.
  useTrialKeys({ onSubmit: onNext, armAfterMs: 400 })

  const answer = Array.isArray(result.correct_answer)
    ? result.correct_answer.join(' … ')
    : result.correct_answer
  const about = result.about

  return (
    <TraceRegion id="T14" note="written the moment the answer arrives">
    <Card style={{
      borderColor: right ? theme.green : theme.orange,
      background: right ? theme.greenPale : theme.orangePale,
    }}>
      <div style={{ fontSize: 34, marginBottom: 8 }}>
        {right ? '✅' : skipped ? '👀' : '💡'}
      </div>
      <div style={{
        fontSize: 22, fontWeight: 800, marginBottom: 8,
        color: right ? theme.greenDarker : theme.orangeDark,
      }}>
        {right ? 'Yes, that is it' : skipped ? 'Here it is' : 'Not quite'}
      </div>

      {!right && answer ? (
        <p style={{ margin: '0 0 6px', fontSize: 18, color: theme.text, lineHeight: 1.5 }}>
          The answer is{' '}
          <Trace id="T10.payload_json" title="the answer stored on this version of the question">
            <strong>{answer}</strong>
          </Trace>.
        </p>
      ) : null}

      {/* A near miss is worth naming — it is different from a wild guess. */}
      {!right && result.edit_distance === 1 ? (
        <p style={{ margin: '0 0 6px', fontSize: 15, color: theme.muted }}>
          <Trace id="T14.edit_distance">You were one letter away.</Trace>
        </p>
      ) : null}

      {/* After a miss, the word itself: what it means, how it is used, how it
          sounds (build list item 1, 25 Oct 2026; three research rounds agree). */}
      {!right && about && (about.definition || about.example) ? (
        <div style={{
          marginTop: 14, padding: '12px 14px', borderRadius: radius.sm,
          background: theme.card, border: `1px solid ${theme.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <Trace id="T3.headword">
              <strong style={{ fontSize: 20, color: theme.text }}>{about.word}</strong>
            </Trace>
            <button
              type="button"
              aria-label={`Hear ${about.word}`}
              title="Hear it"
              onClick={() => say(about.example ? `${about.word}. ${about.example}` : about.word)}
              style={{
                fontFamily: 'inherit', fontSize: 15, fontWeight: 700, lineHeight: 1, cursor: 'pointer', color: theme.text,
                background: 'transparent', border: `1px solid ${theme.border}`,
                borderRadius: radius.sm, padding: '6px 10px',
              }}
            >
              🔊 Hear it
            </button>
          </div>
          {about.definition ? (
            <p style={{ margin: '0 0 6px', fontSize: 16, color: theme.text, lineHeight: 1.5 }}>
              <Trace id="T4.definition">{about.definition}</Trace>
            </p>
          ) : null}
          {about.example ? (
            <p style={{ margin: 0, fontSize: 15, color: theme.muted, lineHeight: 1.5, fontStyle: 'italic' }}>
              <Trace id="T4.example_sentence">{about.example}</Trace>
            </p>
          ) : null}
        </div>
      ) : null}

      <div style={{ marginTop: 18 }}>
        <Button full tone="child" onClick={onNext}>
          {result.finished ? 'See how you did' : 'Next'}
        </Button>
        {HAS_KEYBOARD ? (
          <div style={{
            fontSize: 13, color: theme.faint, textAlign: 'center', marginTop: 10,
          }}>
            Press Enter to carry on
          </div>
        ) : null}
      </div>
    </Card>
    </TraceRegion>
  )
}
